import { useStyletron } from '@thanx/uikit/theme'
import React, { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { Icon, MultiSelectMenu } from 'controlled-components'
import { buildTranslate } from 'locales'
import {
  filterOptionsBySearch,
  selectBottomData,
  selectBottomDisabled,
  selectBottomIsOpen,
  selectBottomSearchTerm,
  selectBottomShowingSelections,
} from 'slices/aiSegmentBuilder/picker'

import HeaderContainer from './Header.container'
import OptionsListContainer from './OptionsList.container'
import SelectionListContainer from './SelectionList.container'

const t = buildTranslate('components.ai.picker')

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
  disabled?: boolean
  isLoading?: boolean
  goToPage?: (page: number) => void
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the dropdown menu.
 * Orchestrates header and list sub-containers.
 * Shows loading spinner while data is being fetched.
 * Only renders when dropdown is open and not disabled.
 */
const MenuContainer: React.FC<Props> = ({
  pickerId,
  disabled = false,
  isLoading = false,
  goToPage,
  isOpen,
  reduxDisabled,
  showingSelections,
  data,
  searchTerm,
}) => {
  const [css, theme] = useStyletron()

  // Derived: combined disabled state (prop OR redux)
  const isDisabled = useMemo(
    () => disabled || reduxDisabled,
    [disabled, reduxDisabled]
  )

  // Derived: an active search that matched nothing in the loaded set. Distinct
  // from the loading / no-server-data states.
  const hasNoSearchMatches = useMemo(
    () =>
      searchTerm.trim() !== '' &&
      filterOptionsBySearch(data, searchTerm).length === 0,
    [data, searchTerm]
  )

  // Don't render if disabled
  if (isDisabled) {
    return null
  }

  const messageContainerClassName = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    color: theme.colors.grey50,
  })

  // Render content based on state:
  // - Showing selections: show selection list (local data, no loading needed)
  // - Loading: show spinner
  // - Search matched nothing: show "no matches" empty state
  // - Otherwise: show options list
  const renderContent = () => {
    if (showingSelections) {
      return <SelectionListContainer pickerId={pickerId} />
    }
    if (isLoading) {
      return (
        <div className={messageContainerClassName}>
          <Icon name="spinner" size={24} spin />
        </div>
      )
    }
    if (hasNoSearchMatches) {
      return (
        <div role="status" className={messageContainerClassName}>
          {t('no_matches')}
        </div>
      )
    }
    return <OptionsListContainer pickerId={pickerId} />
  }

  return (
    <MultiSelectMenu isOpen={isOpen}>
      <HeaderContainer pickerId={pickerId} goToPage={goToPage} />
      {renderContent()}
    </MultiSelectMenu>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  isOpen: selectBottomIsOpen(state, ownProps.pickerId),
  reduxDisabled: selectBottomDisabled(state, ownProps.pickerId),
  showingSelections: selectBottomShowingSelections(state, ownProps.pickerId),
  data: selectBottomData(state, ownProps.pickerId),
  searchTerm: selectBottomSearchTerm(state, ownProps.pickerId),
})

const connector = connect(mapStateToProps)

export default connector(MenuContainer)
